/* eslint-disable react/prop-types */
import { getMenuLanguages, MENU_LANGUAGE_CODES } from "../../../constants/menuLanguages";
import {
  FiPlus,
  FiTrash2,
  FiMoreVertical,
  FiEdit2,
  FiMoreHorizontal,
} from "react-icons/fi";

import PageTitle from "../../layouts/PageTitle";

import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { useNavigate } from "react-router-dom";
import config from "../../../../config.json";
import "../../../i18n";
import { useTranslation } from "react-i18next";
import AddChoice from "./AddChoice";
import EditChoice from "./EditChoice";
import AddItem from "./AddItem";
import {
  assignItemsToChoice,
  removeItemFromChoice,
} from "../../../services/MenuService";
import EditItem from "./EditItem";
import {
  IMAGE_PICKER_ACCEPT,
  validateImageFile,
} from "../../../utils/imageValidation";
import NutritionValuesEditor from "../shared/NutritionValuesEditor";
import ProductBadgesEditor from "../shared/ProductBadgesEditor";
import { normalizeNutritionValuesForSave } from "../../../constants/nutritionOptions";
import { normalizeProductBadgesForSave } from "../../../constants/productBadgeOptions";
import DualPriceFields, { ExtraSizeDualPriceInputs } from "./DualPriceFields";
import { parseOptionalPrice } from "../../../utils/currency";
import WizardLivePhone from "../Setup/StartNowWizard/WizardLivePhone";
import {
  buildProductDetailLivePreview,
  buildProductFormLivePreview,
} from "../Cms/MenuTree/menuTreeUtils";
import "./AddCategory.css";
import "../shared/ProductExtras.css";

const AddNewProduct = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ----------- choices management --------------
  const [choices, setChoices] = useState([]);
  const [filteredChoices, setFilteredChoices] = useState([]);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [selectedItemsByChoice, setSelectedItemsByChoice] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [choiceItemsMap, setChoiceItemsMap] = useState({});
  const [openChoiceMenuId, setOpenChoiceMenuId] = useState(null);

  // ---------- Items management --------------
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchItemTerm, setSearchItemTerm] = useState("");
  const [filteredItems, setfilteredItems] = useState([]);
  const [initialChoiceItems, setInitialChoiceItems] = useState([]);
  const [choiceItems, setChoiceItems] = useState([]);
  const [selectedItemsMap, setSelectedItemsMap] = useState({});
  const [openItemMenuId, setOpenItemMenuId] = useState(null);

  const [activeSection, setActiveSection] = useState("existing");

  // ----------- product added -----------------
  const [createdProductId, setCreatedProductId] = useState(null);
  const [choicesError, setChoicesError] = useState(null);

  // ---------- modals ------------------
  const [showAddChoiceModal, setShowAddChoiceModal] = useState(false);
  const [showEditChoiceModal, setShowEditChoiceModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [editingChoice, setEditingChoice] = useState(null);
  const [editinItem, setEditingItem] = useState(null);

  const languages = useMemo(() => getMenuLanguages(t), [t]);
  const fileInputRef = useRef(null);

  // eslint-disable-next-line no-unused-vars
  const [activeLang, setActiveLang] = useState(languages[0].code);
  const [productDetails, setProductDetails] = useState(() =>
    Object.fromEntries(
      MENU_LANGUAGE_CODES.map((code) => [code, { productTitle: "", productDescription: "" }])
    )
  );
  const [openLangs, setOpenLangs] = useState({ en: true });
  const isLangOpen = (code) => Boolean(openLangs[code]);
  const toggleLang = (code) =>
    setOpenLangs((prev) => ({ ...prev, [code]: !prev[code] }));

  // Loader
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    price: 0,
    secondaryPrice: "",
    rate: 0,
    isMultipleSize: false,
    extraSizes: [{ size: "", finalPrice: "", secondaryFinalPrice: "" }],
    photos: [],
  });

  const [files, setFiles] = useState(Array(4).fill(null));
  const [imageUrls, setImageUrls] = useState(Array(4).fill(null));
  const [imageDisabled, setImageDisabled] = useState(false);
  const [productBadges, setProductBadges] = useState([]);
  const [nutritionValues, setNutritionValues] = useState([]);
  const [tags, setTags] = useState([]);
  const [currentTag, setCurrentTag] = useState("");
  const [step, setStep] = useState(1);

  const livePreview = useMemo(() => {
    const livePrice = formData.isMultipleSize
      ? parseOptionalPrice(formData.extraSizes?.[0]?.finalPrice) ?? 0
      : parseOptionalPrice(formData.price) ?? 0;
    const liveSecondary = formData.isMultipleSize
      ? parseOptionalPrice(formData.extraSizes?.[0]?.secondaryFinalPrice)
      : formData.secondaryPrice;
    return buildProductFormLivePreview({
      productDetails,
      price: livePrice,
      secondaryPrice: liveSecondary,
      photoUrl: imageUrls[0] || null,
      imageDisabled,
    });
  }, [
    productDetails,
    formData.price,
    formData.secondaryPrice,
    formData.isMultipleSize,
    formData.extraSizes,
    imageUrls,
    imageDisabled,
  ]);

  const productDetailPreview = useMemo(() => {
    const livePrice = formData.isMultipleSize
      ? parseOptionalPrice(formData.extraSizes?.[0]?.finalPrice) ?? 0
      : parseOptionalPrice(formData.price) ?? 0;
    const liveSecondary = formData.isMultipleSize
      ? parseOptionalPrice(formData.extraSizes?.[0]?.secondaryFinalPrice)
      : formData.secondaryPrice;
    return buildProductDetailLivePreview({
      productDetails,
      price: livePrice,
      secondaryPrice: liveSecondary,
      photoUrl: imageUrls[0] || null,
      imageDisabled,
      selectedChoiceIds: selectedChoices,
      choices,
      choiceItemsMap,
      selectedItemsByChoice,
    });
  }, [
    productDetails,
    formData.price,
    formData.secondaryPrice,
    formData.isMultipleSize,
    formData.extraSizes,
    imageUrls,
    imageDisabled,
    selectedChoices,
    choices,
    choiceItemsMap,
    selectedItemsByChoice,
  ]);

  const stripHtml = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  // ----------- fetch choices (backend filters by productId + search) --------------
  const fetchChoices = async (productId, search) => {
    try {
      const params = new URLSearchParams();
      if (productId) params.set("productId", productId);
      if (search && search.trim()) params.set("search", search.trim());
      const url = `${config.dev}/v2/choices/retrieve${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
        },
      });
      const data = response?.data || [];
      setChoices(data);
      setFilteredChoices(data);
      setChoicesError(null);
    } catch (err) {
      setChoicesError("Failed to fetch choices. Please try again later.");
      setFilteredChoices([]);
      console.error("Error fetching choices:", err);
    }
  };

  useEffect(() => {
    if (step === 2) {
      fetchChoices(createdProductId || undefined, searchTerm);
    }
  }, [step, createdProductId, searchTerm]);

  useEffect(() => {
    if (step === 2) {
      const filteredItems = items.filter((item) => {
        const title = item.itemDetails?.[0]?.itemTitle?.toLowerCase() || "";
        return title.includes(searchItemTerm.toLowerCase());
      });
      setfilteredItems(filteredItems);
    }
  }, [step, searchItemTerm, items]);

  // ------------ fetch items d'un choice ------------
  const fetchChoiceItemsForChoice = async (choiceId) => {
    try {
      const response = await axios.get(
        `${config.dev}/v2/choices/getAllItems/${choiceId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
          },
        }
      );

      setChoiceItemsMap((prev) => ({
        ...prev,
        [choiceId]: response.data.items,
      }));
    } catch (error) {
      console.error("Erreur lors de la récupération des items du choix", error);
    }
  };

  useEffect(() => {
    if (step === 2) {
      filteredChoices.forEach((choice) => {
        if (!choiceItemsMap[choice._id]) {
          fetchChoiceItemsForChoice(choice._id); // ← Passe bien l'id
        }
      });
    }
  }, [step, filteredChoices]);

  // ------------ fetch all items ----------------
  const fetchItemsList = async () => {
    try {
      setLoading(true);
      const [itemsRes] = await Promise.all([
        axios.get(`${config.dev}/v2/items/retrieve`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
          },
        }),
      ]);
      // Filtrage côté front si le backend ne le fait pas
      const fetchedItems = itemsRes.data.filter((item) => !item.isArchived);
      setItems(fetchedItems);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Erreur fetch choice/items:", error);
    }
  };

  useEffect(() => {
    if (step === 2 && showAddItemModal) {
      fetchItemsList();
    }
  }, [step, showAddItemModal]);

  const handleEditChoice = (choice) => {
    setEditingChoice(choice);
    setShowEditChoiceModal(true);
  };

  const itemIdsForChoice = (choiceId) =>
    (choiceItemsMap[choiceId] || []).map((item) => item._id);

  const handleChoiceToggle = (choiceId) => {
    setSelectedChoices((prev) => {
      const isOn = prev.includes(choiceId);
      if (isOn) {
        setSelectedItemsByChoice((map) => {
          const next = { ...map };
          delete next[choiceId];
          return next;
        });
        return prev.filter((id) => id !== choiceId);
      }
      const itemIds = itemIdsForChoice(choiceId);
      if (itemIds.length) {
        setSelectedItemsByChoice((map) => ({ ...map, [choiceId]: itemIds }));
      }
      if (!choiceItemsMap[choiceId]) fetchChoiceItemsForChoice(choiceId);
      return [...prev, choiceId];
    });
  };

  const openAssignItemsForChoice = (choiceId) => {
    setOpenChoiceMenuId(null);
    setSelectedChoices((prev) => (prev.includes(choiceId) ? prev : [...prev, choiceId]));
    const itemIds = itemIdsForChoice(choiceId);
    if (itemIds.length) {
      setSelectedItemsByChoice((map) =>
        map[choiceId] !== undefined ? map : { ...map, [choiceId]: itemIds }
      );
    }
    if (!choiceItemsMap[choiceId]) fetchChoiceItemsForChoice(choiceId);
    setShowAddItemModal(choiceId);
  };

  const handleChoiceItemToggle = (choiceId, itemId) => {
    const allIds = itemIdsForChoice(choiceId);
    setSelectedItemsByChoice((map) => {
      const current = map[choiceId] ?? allIds;
      const nextIds = current.includes(itemId)
        ? current.filter((id) => id !== itemId)
        : [...current, itemId];

      if (nextIds.length === 0) {
        setSelectedChoices((prev) => prev.filter((id) => id !== choiceId));
        const next = { ...map };
        delete next[choiceId];
        return next;
      }

      if (!selectedChoices.includes(choiceId)) {
        setSelectedChoices((prev) =>
          prev.includes(choiceId) ? prev : [...prev, choiceId]
        );
      }
      return { ...map, [choiceId]: nextIds };
    });
  };

  useEffect(() => {
    if (step !== 2) return;
    setSelectedItemsByChoice((map) => {
      let changed = false;
      const next = { ...map };
      selectedChoices.forEach((choiceId) => {
        if (next[choiceId] !== undefined) return;
        const ids = (choiceItemsMap[choiceId] || []).map((item) => item._id);
        if (!ids.length) return;
        next[choiceId] = ids;
        changed = true;
      });
      return changed ? next : map;
    });
  }, [step, selectedChoices, choiceItemsMap]);

  // Gérer les changements de langues
  const handleTabSelect = (key) => setActiveLang(key);

  // Gérer les changements des champs par langue
  const handleLangInputChange = (lang, field, value) => {
    setProductDetails({
      ...productDetails,
      [lang]: {
        ...productDetails[lang],
        [field]: value,
      },
    });
  };

  const handleAddTag = () => {
    if (currentTag && !tags.includes(currentTag)) {
      setTags([...tags, currentTag]);
      setCurrentTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const normalizedNutritionValues = normalizeNutritionValuesForSave(nutritionValues);
  const normalizedProductBadges = normalizeProductBadgesForSave(productBadges);

  // ----------- Add new product & pass to step 2 -----------------
  const handleSubmit = async (e) => {
    // Construction des extraSizes valides
    const extraSizes = formData.extraSizes
      .filter(
        (size) =>
          size.size ||
          parseOptionalPrice(size.finalPrice) != null ||
          parseOptionalPrice(size.secondaryFinalPrice) != null
      )
      .map((size) => ({
        size: size.size,
        finalPrice: parseOptionalPrice(size.finalPrice) ?? 0,
        secondaryFinalPrice: parseOptionalPrice(size.secondaryFinalPrice),
      }));

    // Construction des détails du produit
    const productDetailsToSend = Object.entries(productDetails).map(
      ([lang, details]) => ({
        language: lang,
        productTitle: stripHtml(details.productTitle?.trim() ?? "") || "",
        productDescription: stripHtml(details.productDescription?.trim() ?? "") || "",
      })
    );

    const hasTitle = productDetailsToSend.some(
      (d) => String(d.productTitle || "").trim().length > 0
    );
    if (!hasTitle) {
      swal(
        t("warningTitle"),
        t("productTitleRequired") || "Product title is required.",
        "warning"
      );
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append(
      "data",
      JSON.stringify({
        price: parseOptionalPrice(formData.price) ?? 0,
        secondaryPrice: parseOptionalPrice(formData.secondaryPrice),
        rate: parseFloat(formData.rate),
        haveMultipleSize: formData.isMultipleSize || false,
        extraSizes,
        productDetails: productDetailsToSend,
        tags: tags,
        productBadges: normalizedProductBadges,
        productBadge: "",
        nutritionValues: normalizedNutritionValues,
        imageDisabled,
        // preparationTime,
      })
    );

    // Ajouter les fichiers au FormData
    files.forEach((file) => {
      if (file) formDataToSend.append("file", file);
    });

    // Envoi des données à l'API
    try {
      const response = await axios.post(
        `${config.dev}/v2/products/create`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
          },
        }
      );
      // swal({
      //   title: t("successTitle"),
      //   text: t("productCreated"),
      //   icon: "success",
      //   timer: 1000, // durée en millisecondes (ici 2 secondes)
      //   buttons: false, // pour ne pas afficher de bouton "OK"
      // });
      // setTimeout(() => {
      //   navigate("/products");
      // }, 500);

      const productId = response.data?._id;
      setCreatedProductId(productId);

      console.log("dernier produit", response.data);

      swal({
        title: t("successTitle"),
        text: t("productCreated"),
        icon: "success",
        timer: 1000,
        buttons: false,
      }).then(() => {
        setStep(2);
      });
    } catch (error) {
      console.error(
        "Erreur lors de l'ajout du produit :",
        error.response?.data || error.message
      );
      alert(t("failedAddProduct"));
    }
  };

  // ----------- add choice to product -------------------------
  const addChoiceToProduct = async () => {
    if (!createdProductId) {
      swal(t("errorTitle"), t("productNotFound"), "error");
      return;
    }

    if (selectedChoices.length === 0) {
      navigate("/products");
      return;
    }
    const token = localStorage.getItem("tokenLogin");

    try {
      await axios.post(
        `${config.dev}/v2/products/addChoice/${createdProductId}`,
        {
          choiceList: selectedChoices,
          hiddenChoiceItems: selectedChoices
            .map((choiceId) => {
              const allIds = itemIdsForChoice(choiceId);
              const kept = selectedItemsByChoice[choiceId] ?? allIds;
              const hidden = allIds.filter((id) => !kept.includes(id));
              return hidden.length ? { choice: choiceId, items: hidden } : null;
            })
            .filter(Boolean),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      swal({
        title: t("successTitle"),
        text: t("choiceLinked"),
        icon: "success",
        timer: 1000,
        buttons: false,
      }).then(() => {
        // fin du wizard
        navigate("/products");
      });
    } catch (error) {
      console.error("Error linking choices:", error);
      swal(
        t("errorTitle"),
        error.response?.data?.message || t("FailedLinkChoiceToProduct"),
        "error"
      );
    }
  };

  // Gérer les fichiers (images)
  const handleFileChange = (event, index) => {
    const selectedFile = event.target.files[0];
    event.target.value = "";
    if (!selectedFile) return;

    const { isValid, message } = validateImageFile(selectedFile, 5);
    if (!isValid) {
      swal("Warning", message, "warning");
      return;
    }

    const updatedFiles = [...files];
    const updatedUrls = [...imageUrls];
    if (updatedUrls[index]) URL.revokeObjectURL(updatedUrls[index]);
    updatedFiles[index] = selectedFile;
    updatedUrls[index] = URL.createObjectURL(selectedFile);
    setFiles(updatedFiles);
    setImageUrls(updatedUrls);
  };

  const handleRemoveImage = (index) => {
    setImageUrls((prev) => {
      const next = [...prev];
      if (next[index]) URL.revokeObjectURL(next[index]);
      next[index] = null;
      return next;
    });
    setFiles((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  };

  const navigateTo = (e) => {
    e.preventDefault();
    navigate("/products");
  };

  // -------- gestion items d'un choice -----------
  const handleToggleItemMenu = (itemId) => {
    setOpenItemMenuId((prev) => (prev === itemId ? null : itemId));
    // Close choice menu when opening item menu
    setOpenChoiceMenuId(null);
  };

  const handleEditItem = (item) => {
    // setOpenItemMenuId(null);
    setEditingItem(item);
    setShowEditItemModal(true);
  };

  const handleDeleteItem = (itemId) => {
    setOpenItemMenuId(null);
    console.log("Delete item:", itemId);
  };

  // ----------- gestion choices --------------
  const handleToggleChoiceMenu = (choiceId) => {
    setOpenChoiceMenuId((prev) => (prev === choiceId ? null : choiceId));
    setOpenItemMenuId(null);
  };

  useEffect(() => {
    if (!openChoiceMenuId && !openItemMenuId) return undefined;
    const closeMenus = () => {
      setOpenChoiceMenuId(null);
      setOpenItemMenuId(null);
    };
    document.addEventListener("click", closeMenus);
    return () => document.removeEventListener("click", closeMenus);
  }, [openChoiceMenuId, openItemMenuId]);

  const handleAddItemFromChoice = (choiceId) => {
    setOpenChoiceMenuId(null);
    setShowAddItemModal(choiceId);
  };

  const handleArchiveChoice = (choiceId) => {
    setOpenChoiceMenuId(null);
    console.log("Archive choice:", choiceId);
  };

  const openLanguages = languages.filter((lang) => isLangOpen(lang.code));

  const langFields = (lang) => (
    <div className="catform__lang-panel" key={lang.code}>
      <div className="catform__field">
        <label className="catform__label" htmlFor={`prod-title-${lang.code}`}>
          <img src={lang.flag} alt="" />
          {lang.label} — {t("title")}
        </label>
        <input
          id={`prod-title-${lang.code}`}
          type="text"
          className="catform__input"
          value={productDetails[lang.code]?.productTitle || ""}
          autoFocus={lang.code === "en" && openLanguages.length === 1}
          onChange={(e) => handleLangInputChange(lang.code, "productTitle", e.target.value)}
        />
      </div>
      <div className="catform__field">
        <label className="catform__label" htmlFor={`prod-desc-${lang.code}`}>
          {t("description")}
        </label>
        <textarea
          id={`prod-desc-${lang.code}`}
          className="catform__input catform__textarea"
          rows={3}
          value={productDetails[lang.code]?.productDescription || ""}
          onChange={(e) => handleLangInputChange(lang.code, "productDescription", e.target.value)}
        />
      </div>
    </div>
  );

  return (
    <>
      <PageTitle activeMenu={t("addProduct")} motherMenu={t("kitchen")} />

      <div className="prod-stepper">
        <div className={`prod-stepper__item${step === 1 ? " is-active" : " is-done"}`}>
          <span className="prod-stepper__dot">1</span>
          <span className="prod-stepper__label">{t("prodFormTitle")}</span>
        </div>
        <div className={`prod-stepper__line${step === 2 ? " is-done" : ""}`} />
        <div className={`prod-stepper__item${step === 2 ? " is-active" : ""}`}>
          <span className="prod-stepper__dot">2</span>
          <span className="prod-stepper__label">{t("prodFormChoices")}</span>
        </div>
      </div>

      <div className="catform-with-preview">
      {step === 1 && (
        <>
          <form
            className="catform"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            noValidate
          >
            <div className="catform__top">
              <h1 className="catform__title">{t("prodFormTitle")}</h1>
              <button type="button" className="btn catform__link" onClick={navigateTo}>
                <i className="fas fa-arrow-left" aria-hidden /> {t("productsList")}
              </button>
            </div>

            <section className="catform__section">
              <h2 className="catform__heading">
                {t("prodFormPhoto")} <span className="catform__optional">{t("optional")}</span>
              </h2>
              <input
                ref={fileInputRef}
                hidden
                type="file"
                accept={IMAGE_PICKER_ACCEPT}
                onChange={(e) => handleFileChange(e, 0)}
              />
              <div className="catform__photo">
                <button
                  type="button"
                  className="catform__thumb"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {imageUrls[0] ? <img src={imageUrls[0]} alt="" /> : <i className="fas fa-camera" aria-hidden />}
                </button>
                <div className="catform__photo-actions">
                  <button
                    type="button"
                    className="btn catform__small"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {imageUrls[0] ? t("catFormReplacePhoto") : t("catFormChoosePhoto")}
                  </button>
                  {imageUrls[0] && (
                    <button
                      type="button"
                      className="btn catform__small is-danger"
                      onClick={() => handleRemoveImage(0)}
                    >
                      {t("catFormRemovePhoto")}
                    </button>
                  )}
                  <label className={`catform__radio ${imageDisabled ? "is-on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={imageDisabled}
                      onChange={(e) => setImageDisabled(e.target.checked)}
                    />
                    {t("disableImage")}
                  </label>
                </div>
              </div>
            </section>

            <section className="catform__section">
              <h2 className="catform__heading">{t("catFormStepName")}</h2>
              <p className="catform__pills-label">{t("prodFormChooseLanguage")}</p>
              <div className="catform__pills">
                {languages.map((lang) => {
                  const filled = Boolean(
                    productDetails[lang.code]?.productTitle?.trim() ||
                      productDetails[lang.code]?.productDescription?.trim()
                  );
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      className={`catform__pill ${isLangOpen(lang.code) ? "is-open" : ""} ${filled ? "is-filled" : ""}`}
                      aria-pressed={isLangOpen(lang.code)}
                      onClick={() => toggleLang(lang.code)}
                    >
                      <img src={lang.flag} alt="" />
                      {lang.label}
                      {filled && <i className="fas fa-check" aria-hidden />}
                    </button>
                  );
                })}
              </div>
              <div className="catform__lang-fields">
                {openLanguages.map((lang) => langFields(lang))}
              </div>
            </section>

            <section className="catform__section">
              <h2 className="catform__heading">{t("prodFormPrice")}</h2>
              <DualPriceFields
                price={formData.price}
                secondaryPrice={formData.secondaryPrice}
                disabled={formData.isMultipleSize}
                onPriceChange={(value) => setFormData({ ...formData, price: value })}
                onSecondaryPriceChange={(value) =>
                  setFormData({ ...formData, secondaryPrice: value })
                }
              />
              <label className={`catform__radio catform__radio--check ${formData.isMultipleSize ? "is-on" : ""}`}>
                <input
                  type="checkbox"
                  checked={formData.isMultipleSize}
                  onChange={() =>
                    setFormData({ ...formData, isMultipleSize: !formData.isMultipleSize })
                  }
                />
                {t("haveMultipleSizes")}
              </label>

              {formData.isMultipleSize && (
                <div className="catform__sizes">
                  {formData.extraSizes.map((size, index) => (
                    <div key={index} className="catform__size-row">
                      <input
                        type="text"
                        className="catform__input"
                        placeholder={t("prodFormSize")}
                        value={size.size}
                        onChange={(e) => {
                          const newSizes = [...formData.extraSizes];
                          newSizes[index].size = e.target.value;
                          setFormData({ ...formData, extraSizes: newSizes });
                        }}
                      />
                      <ExtraSizeDualPriceInputs
                        size={size}
                        onFinalPriceChange={(value) => {
                          const newSizes = [...formData.extraSizes];
                          newSizes[index].finalPrice = value;
                          setFormData({ ...formData, extraSizes: newSizes });
                        }}
                        onSecondaryFinalPriceChange={(value) => {
                          const newSizes = [...formData.extraSizes];
                          newSizes[index].secondaryFinalPrice = value;
                          setFormData({ ...formData, extraSizes: newSizes });
                        }}
                      />
                      <button
                        type="button"
                        className="btn catform__small is-danger"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            extraSizes: formData.extraSizes.filter((_, i) => i !== index),
                          });
                        }}
                      >
                        <i className="fa fa-trash" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn catform__small"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        extraSizes: [
                          ...formData.extraSizes,
                          { size: "", finalPrice: 0, secondaryFinalPrice: "" },
                        ],
                      })
                    }
                  >
                    <i className="fas fa-plus" aria-hidden /> {t("extraSizes")}
                  </button>
                </div>
              )}
            </section>

            <section className="catform__section">
              <h2 className="catform__heading">
                {t("prodFormDetails")} <span className="catform__optional">{t("optional")}</span>
              </h2>
              <ProductBadgesEditor values={productBadges} onChange={setProductBadges} />
              <NutritionValuesEditor values={nutritionValues} onChange={setNutritionValues} />
              <div className="prod-extra-tags">
                <div className="prod-extra-tags__head">
                  <span className="prod-extra-trigger__icon" aria-hidden>
                    <i className="fas fa-tags" />
                  </span>
                  <span className="prod-extra-tags__copy">
                    <span className="prod-extra-tags__title">{t("tags") || "Tags"}</span>
                    <span className="prod-extra-tags__hint">{t("prodFormTagsHint")}</span>
                  </span>
                </div>
                <div className="catform__tag-row">
                  <input
                    type="text"
                    className="catform__input"
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTag())}
                    placeholder={t("prodFormTagPlaceholder")}
                  />
                  <button type="button" className="btn catform__small" onClick={handleAddTag}>
                    {t("prodFormAddTag")}
                  </button>
                </div>
                {tags.length > 0 && (
                  <div className="catform__chips">
                    {tags.map((tag) => (
                      <span key={tag} className="catform__chip">
                        {tag}
                        <button type="button" aria-label={t("remove")} onClick={() => handleRemoveTag(tag)}>
                          <i className="fas fa-times" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <div className="catform__actions">
              <button type="button" className="btn catform__btn catform__btn--cancel" onClick={navigateTo}>
                {t("cancel")}
              </button>
              <button type="submit" className="btn catform__btn catform__btn--success">
                {t("addProduct")}
              </button>
            </div>
          </form>
        </>
      )}

      {step === 2 && (
        <div className="catform">
          <div className="catform__top">
            <div>
              <h1 className="catform__title">{t("prodFormChoices")}</h1>
              <p className="catform__help">{t("choicesHint")}</p>
            </div>
            <button
              type="button"
              className="btn catform__btn catform__btn--success"
              onClick={() => setShowAddChoiceModal(true)}
            >
              <i className="fas fa-plus" aria-hidden /> {t("addChoice")}
            </button>
          </div>

          <section className="catform__section is-open">
            <h2 className="catform__heading">
              {t("selectChoice").trim()}
              {selectedChoices.length > 0 && (
                <span className="catform__fold-count">{selectedChoices.length}</span>
              )}
              <span className="catform__optional">{t("optional")}</span>
            </h2>

            <div className="prod-choices__toolbar">
              <div className="prod-choices__search">
                <i className="fas fa-search prod-choices__search-icon" aria-hidden />
                <input
                  type="text"
                  className="catform__input"
                  placeholder={`${t("searchChoice")}…`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm ? (
                  <button
                    type="button"
                    className="prod-choices__search-clear"
                    onClick={() => setSearchTerm("")}
                    aria-label={t("cancel")}
                  >
                    <i className="fas fa-times" />
                  </button>
                ) : null}
              </div>
            </div>

            <div className="prod-choices__list">
              {filteredChoices.length === 0 ? (
                <div className="prod-choices__empty">
                  <i className="fas fa-layer-group" aria-hidden />
                  <p>{searchTerm ? t("noChoicesFound") : t("choiceFormEmpty")}</p>
                  {!searchTerm && (
                    <button
                      type="button"
                      className="btn catform__small"
                      onClick={() => setShowAddChoiceModal(true)}
                    >
                      <i className="fas fa-plus" aria-hidden /> {t("addChoice")}
                    </button>
                  )}
                </div>
              ) : (
                filteredChoices.map((choice) => {
                  const title =
                    choice.choiceDetails?.[0]?.choiceTitle || "Unnamed choice";
                  const choiceItems = choiceItemsMap[choice._id] || [];
                  const isChecked = selectedChoices.includes(choice._id);
                  const keptItemIds = selectedItemsByChoice[choice._id] ?? (isChecked
                    ? choiceItems.map((item) => item._id)
                    : []);

                  return (
                    <article
                      key={choice._id}
                      className={`prod-choice${isChecked ? " is-checked is-open" : ""}`}
                    >
                      <div className="prod-choice__head">
                        <label className="prod-choice__check">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleChoiceToggle(choice._id)}
                          />
                          <span className="prod-choice__name">{title}</span>
                        </label>
                        {choiceItems.length > 0 && (
                          <span className="prod-choice__count">
                            {isChecked ? `${keptItemIds.length}/${choiceItems.length}` : choiceItems.length}
                          </span>
                        )}
                        {choiceItems.length > 0 && (
                          <i
                            className={`fas fa-chevron-down prod-choice__chevron${isChecked ? " is-open" : ""}`}
                            aria-hidden
                          />
                        )}
                        <div className="prod-choice__more">
                          <button
                            type="button"
                            className="prod-choice__icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleChoiceMenu(choice._id);
                            }}
                            aria-label={t("editChoice")}
                          >
                            <FiMoreHorizontal size={16} />
                          </button>
                          {openChoiceMenuId === choice._id && (
                            <div
                              className="prod-choice__menu"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenChoiceMenuId(null);
                                  handleEditChoice(choice);
                                }}
                              >
                                <FiEdit2 size={14} />
                                {t("editChoice")}
                              </button>
                              <button
                                type="button"
                                onClick={() => openAssignItemsForChoice(choice._id)}
                              >
                                <FiPlus size={14} />
                                {t("assignItems")}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {!isChecked && choiceItems.length > 0 && (
                        <div className="prod-choice__preview">
                          {choiceItems.slice(0, 3).map((item) => (
                            <span key={item._id} className="prod-choice__chip">
                              {item.itemDetails?.[0]?.itemTitle || "Unnamed item"}
                            </span>
                          ))}
                          {choiceItems.length > 3 && (
                            <span className="prod-choice__chip">+{choiceItems.length - 3}</span>
                          )}
                        </div>
                      )}

                      {isChecked && (
                      <div className="prod-choice__body">
                        {choiceItems.map((item) => {
                          const itemOn = keptItemIds.includes(item._id);
                          return (
                          <div key={item._id} className={`prod-choice__item${itemOn ? " is-checked" : ""}`}>
                            <label className="prod-choice__check">
                              <input
                                type="checkbox"
                                checked={itemOn}
                                onChange={() => handleChoiceItemToggle(choice._id, item._id)}
                              />
                              <span className="prod-choice__item-name">
                                {item.itemDetails?.[0]?.itemTitle || "Unnamed item"}
                              </span>
                            </label>
                            <div className="prod-choice__more">
                              <button
                                type="button"
                                className="prod-choice__icon-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleItemMenu(item._id);
                                }}
                                aria-label={t("editItem")}
                              >
                                <FiMoreVertical size={16} />
                              </button>
                              {openItemMenuId === item._id && (
                                <div
                                  className="prod-choice__menu"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOpenItemMenuId(null);
                                      handleEditItem(item);
                                    }}
                                  >
                                    <FiEdit2 size={14} />
                                    {t("editItem")}
                                  </button>
                                  <button
                                    type="button"
                                    className="is-danger"
                                    onClick={async () => {
                                      setOpenItemMenuId(null);
                                      try {
                                        await removeItemFromChoice(choice._id, item._id);
                                        await fetchChoiceItemsForChoice(choice._id);
                                      } catch (error) {
                                        console.error(
                                          "Erreur lors de la suppression de l'item :",
                                          error
                                        );
                                      }
                                    }}
                                  >
                                    <FiTrash2 size={14} />
                                    {t("deleteItem")}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          );
                        })}
                        <button
                          type="button"
                          className="prod-choice__assign"
                          onClick={() => setShowAddItemModal(choice._id)}
                        >
                          <FiPlus size={12} />
                          {t("assignItems")}
                        </button>
                      </div>
                      )}
                    </article>
                  );
                })
              )}
            </div>
          </section>

          <div className="catform__actions">
            <button
              type="button"
              className="btn catform__btn catform__btn--cancel"
              onClick={() => navigate("/products")}
            >
              {t("prodFormSkip")}
            </button>
            <button
              type="button"
              className="btn catform__btn catform__btn--primary"
              onClick={() => addChoiceToProduct()}
            >
              {t("prodFormDone")}
            </button>
          </div>
        </div>
      )}

      <aside className="catform-live" aria-label={t("wizardPreviewLive")}>
        <WizardLivePhone
          draft={livePreview.draft}
          productPhotos={livePreview.productPhotos}
          compact
          showStepLabel={false}
          emptyHintKey="prodFormLiveEmpty"
          productDetail={step === 2 ? productDetailPreview : null}
        />
      </aside>
      </div>

      {/* ---------- Modals --------------*/}
      {showAddChoiceModal && (
        <div className="modal-overlay" onClick={() => setShowAddChoiceModal(false)}>
          <div className="modal-container prod-modal prod-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t("addChoice")}</h5>
              <button className="btn-close" onClick={() => setShowAddChoiceModal(false)} />
            </div>
            <div className="modal-body">
              <AddChoice
                onSuccess={() => {
                  fetchChoices();
                  setShowAddChoiceModal(false);
                }}
                onClose={() => setShowAddChoiceModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {showEditChoiceModal && (
        <div className="modal-overlay" onClick={() => setShowEditChoiceModal(false)}>
          <div className="modal-container prod-modal prod-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t("editChoice")}</h5>
              <button className="btn-close" onClick={() => setShowEditChoiceModal(false)} />
            </div>
            <div className="modal-body">
              <EditChoice
                open={showEditChoiceModal}
                choiceId={editingChoice?._id}
                choice={editingChoice}
                onClose={() => setShowEditChoiceModal(false)}
                onUpdated={fetchChoices}
              />
            </div>
          </div>
        </div>
      )}

      {showAddItemModal && (
        <div className="modal-overlay" onClick={() => setShowAddItemModal(false)}>
          <div className="modal-container prod-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t("assignItems")}</h5>
              <button className="btn-close" onClick={() => setShowAddItemModal(false)} />
            </div>
            <div className="modal-body">
              <div className="prod-modal__tabs">
                <button
                  type="button"
                  className={`prod-modal__tab${activeSection === "existing" ? " is-active" : ""}`}
                  onClick={() => setActiveSection("existing")}
                >
                  {t("itemsList")} ({items.length})
                </button>
                <button
                  type="button"
                  className={`prod-modal__tab${activeSection === "new" ? " is-active" : ""}`}
                  onClick={() => setActiveSection("new")}
                >
                  {t("addItem")}
                </button>
              </div>

              {activeSection === "existing" && (
                <div>
                  {loading ? (
                    <p className="catform__empty">{t("loading")}...</p>
                  ) : items.length === 0 ? (
                    <div className="prod-choices__empty">
                      <i className="fas fa-utensils" aria-hidden />
                      <p>{t("itemFormEmpty")}</p>
                      <button
                        type="button"
                        className="btn catform__small"
                        onClick={() => setActiveSection("new")}
                      >
                        <i className="fas fa-plus" aria-hidden /> {t("addItem")}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="prod-choices__search">
                        <i className="fas fa-search prod-choices__search-icon" aria-hidden />
                        <input
                          type="text"
                          className="catform__input"
                          placeholder={`${t("searchItem")}…`}
                          value={searchItemTerm}
                          onChange={(e) => setSearchItemTerm(e.target.value)}
                        />
                        {searchItemTerm ? (
                          <button
                            type="button"
                            className="prod-choices__search-clear"
                            onClick={() => setSearchItemTerm("")}
                          >
                            <i className="fas fa-times" />
                          </button>
                        ) : null}
                      </div>
                      <div className="prod-modal__pick">
                        {filteredItems
                          .filter(
                            (item) =>
                              !choiceItemsMap[showAddItemModal]?.some(
                                (assigned) => assigned._id === item._id
                              )
                          )
                          .map((item) => {
                            const isSelected = selectedItems.includes(item._id);
                            const itemTitle =
                              item.itemDetails?.find((d) => d.language === activeLang)?.itemTitle ||
                              item.itemDetails?.[0]?.itemTitle ||
                              "Unnamed item";
                            return (
                              <label
                                key={item._id}
                                className={`prod-modal__option${isSelected ? " is-checked" : ""}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {
                                    setSelectedItems((prev) =>
                                      prev.includes(item._id)
                                        ? prev.filter((id) => id !== item._id)
                                        : [...prev, item._id]
                                    );
                                  }}
                                />
                                <span>{itemTitle}</span>
                              </label>
                            );
                          })}
                      </div>
                    </>
                  )}

                  {selectedItems.length > 0 && (
                    <div className="catform__actions" style={{ borderTop: "none", paddingTop: "0.9rem" }}>
                      <button
                        type="button"
                        className="btn catform__btn catform__btn--primary"
                        onClick={async () => {
                          try {
                            const choiceId = showAddItemModal;
                            await assignItemsToChoice(choiceId, selectedItems);
                            setSelectedChoices((prev) =>
                              prev.includes(choiceId) ? prev : [...prev, choiceId]
                            );
                            setSelectedItemsByChoice((map) => ({
                              ...map,
                              [choiceId]: Array.from(
                                new Set([...(map[choiceId] || []), ...selectedItems])
                              ),
                            }));
                            setSelectedItems([]);
                            await fetchChoiceItemsForChoice(choiceId);
                            setActiveSection("existing");
                            setShowAddItemModal(false);
                          } catch (error) {
                            console.error(error);
                          }
                        }}
                      >
                        {t("prodFormDone")}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeSection === "new" && (
                <AddItem
                  onSuccess={() => {
                    fetchItemsList();
                    setActiveSection("existing");
                  }}
                  onClose={() => setShowAddItemModal(false)}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {showEditItemModal && (
        <div className="modal-overlay" onClick={() => setShowEditItemModal(false)}>
          <div className="modal-container prod-modal prod-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t("editItem")}</h5>
              <button className="btn-close" onClick={() => setShowEditItemModal(false)} />
            </div>

            {/* Body */}
            <div className="modal-body">
              <EditItem
                open={showEditItemModal}
                itemId={editinItem?._id} // état correct
                item={editinItem} // état correct
                onClose={() => setShowEditItemModal(false)}
                onUpdated={async () => {
                  try {
                    // Rafraîchir tous les items
                    await fetchItemsList();

                    // Recalculer filteredItems après fetch
                    setfilteredItems(
                      items.filter((item) =>
                        item.itemDetails?.[0]?.itemTitle
                          .toLowerCase()
                          .includes(searchItemTerm.toLowerCase())
                      )
                    );

                    // Rafraîchir les items assignés à la choice
                    if (editingChoice?._id) {
                      await fetchChoiceItemsForChoice(editingChoice._id);
                    }

                    // Fermer le modal
                    setShowEditItemModal(false);
                  } catch (error) {
                    console.error(
                      "Erreur lors de la mise à jour des items :",
                      error
                    );
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddNewProduct;
